import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { useCase } from "../useCase";
import { usePractice } from "../usePractice";
import { getBrand } from "../utils/brand";
import { createBodyBagTagObjectURL } from "../services/bodyBagTag";

function PracticeCase() {
  const { practice, isLoadingPractice } = usePractice();
  const { caseData, isLoadingCase } = useCase();
  const brand = getBrand(practice?.fields?.brand);
  const casePdfUrl = caseData?.fields?.casePDF?.[0]?.url;
  const [bodyBagTagUrl, setBodyBagTagUrl] = useState(null);
  const [isLoadingTag, setIsLoadingTag] = useState(false);
  const [tagError, setTagError] = useState(false);

  useEffect(() => {
    if (!caseData?.fields || !practice?.fields || isLoadingPractice) return;

    let objectUrl;
    let cancelled = false;

    async function loadTag() {
      setIsLoadingTag(true);
      setTagError(false);
      try {
        const fields = caseData.fields;
        objectUrl = await createBodyBagTagObjectURL({
          tagNumber: fields.tagNumber,
          petName: fields.petName,
          species: fields.species,
          otherSpecies: fields.otherSpecies,
          weight: fields.weight,
          aquamationType: fields.aquamationType,
          ownerName: fields.ownerName,
          submitterInfo: fields.submitterInfo,
          practiceName: practice.fields.practiceName,
          brand: getBrand(practice.fields.brand),
        });
        if (cancelled) {
          URL.revokeObjectURL(objectUrl);
          return;
        }
        setBodyBagTagUrl(objectUrl);
      } catch (error) {
        console.log(error);
        if (!cancelled) setTagError(true);
      } finally {
        if (!cancelled) setIsLoadingTag(false);
      }
    }

    loadTag();

    return () => {
      cancelled = true;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [caseData, practice, isLoadingPractice]);

  if (isLoadingCase)
    return <div className="loading-screen regular-2xl">Loading...</div>;

  if (!caseData) return <div className="container">Case not found</div>;

  return (
    <div className="container">
      <Helmet>
        <title>{brand.name} - Case</title>
      </Helmet>

      <div className="container-inner">
        {isLoadingPractice ? (
          "Loading..."
        ) : !practice?.fields ? (
          <p className="input-error">
            Could not load this practice. Please refresh and try again.
          </p>
        ) : (
          <>
            <p data-cy="case-submission-success">
              {`Thank you for your submission. Your body bag tag is below. A
              copy of the case form has also been sent to ${practice.fields.formReceiptEmail}.`}
            </p>
            <p className="gap-1 mt-2">
              <Link className="button" to={`/${practice.fields.slug}`}>
                Dashboard
              </Link>

              <Link className="button" to={`/${practice.fields.slug}/new-case`}>
                New Case
              </Link>
              {casePdfUrl && (
                <a
                  className="button"
                  href={casePdfUrl}
                  target="_blank"
                  rel="noreferrer"
                >
                  Download Case Details
                </a>
              )}
              {bodyBagTagUrl && (
                <a
                  className="button"
                  href={bodyBagTagUrl}
                  target="_blank"
                  rel="noreferrer"
                  data-cy="download-body-bag-tag-button"
                >
                  Download Body Bag Tag
                </a>
              )}
            </p>

            {isLoadingTag && (
              <div className="mt-2">Preparing body bag tag...</div>
            )}
            {tagError && (
              <div className="mt-2">
                There was an error creating the body bag tag. You can still
                download the case PDF above.
              </div>
            )}
            {bodyBagTagUrl && (
              <div className="pdf-letter-container mt-2">
                <object data={bodyBagTagUrl}>Body bag tag PDF</object>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}

export default PracticeCase;
